export type FieldMappingDraftFieldPatch = {
  op?: 'upsert' | 'delete'
  sources?: string | string[] | null
  defaultValue?: unknown | null
}

export type FieldMappingDraftPatchesV3 = Record<string, Record<string, FieldMappingDraftFieldPatch>>

export function applyFieldMappingDraftOverlay(
  publishedConfig: Record<string, any>,
  patches: FieldMappingDraftPatchesV3 | null | undefined,
): Record<string, any> {
  const effective = cloneConfig(publishedConfig)
  if (!patches || typeof patches !== 'object') return effective

  if (!effective.mapping_config || typeof effective.mapping_config !== 'object') {
    effective.mapping_config = {}
  }

  for (const [segmentId, segmentPatches] of Object.entries(patches)) {
    if (!segmentPatches || typeof segmentPatches !== 'object') continue
    const segment = ensureSegment(effective, segmentId)
    const mappings = segment.field_mapping.mappings

    for (const [fieldKey, patch] of Object.entries(segmentPatches)) {
      if (!fieldKey || !patch || typeof patch !== 'object') continue
      const mappingIndex = mappings.findIndex((item: any) => item?.field === fieldKey)
      if (patch.op === 'delete') {
        if (mappingIndex >= 0) mappings.splice(mappingIndex, 1)
        continue
      }

      let mapping = mappingIndex >= 0 ? mappings[mappingIndex] : undefined
      if (!mapping) {
        mapping = {
          field: fieldKey,
          sources: [],
          required: false,
          policy: { on_missing: 'fallback' },
        }
        mappings.push(mapping)
      }

      if (Object.prototype.hasOwnProperty.call(patch, 'sources')) {
        mapping.sources = normalizePatchSources(patch.sources)
      }

      if (Object.prototype.hasOwnProperty.call(patch, 'defaultValue')) {
        if (patch.defaultValue === null || patch.defaultValue === undefined) {
          delete mapping.default_value
          if (mapping.policy && typeof mapping.policy === 'object') {
            delete mapping.policy.default_value
          }
        } else {
          mapping.default_value = patch.defaultValue
          mapping.policy = {
            ...(mapping.policy || { on_missing: 'fallback' }),
            default_value: patch.defaultValue,
          }
        }
      }
    }

    if (
      segmentId !== 'all' &&
      mappings.length === 0 &&
      effective.mapping_config[segmentId]
    ) {
      delete effective.mapping_config[segmentId]
    }
  }

  return effective
}

export function collectDraftFieldMappingFieldsForSegment(
  patches: FieldMappingDraftPatchesV3 | null | undefined,
  segmentId: string,
): string[] {
  const segmentPatches = patches?.[segmentId]
  if (!segmentPatches || typeof segmentPatches !== 'object') return []
  return Object.keys(segmentPatches)
    .filter((field) => field.trim().length > 0)
    .sort((a, b) => a.localeCompare(b))
}

function ensureSegment(config: Record<string, any>, segmentId: string) {
  if (!config.mapping_config[segmentId] || typeof config.mapping_config[segmentId] !== 'object') {
    config.mapping_config[segmentId] = { field_mapping: { mappings: [] } }
  }
  const segment = config.mapping_config[segmentId]
  if (!segment.field_mapping || typeof segment.field_mapping !== 'object') {
    segment.field_mapping = { mappings: [] }
  }
  if (!Array.isArray(segment.field_mapping.mappings)) {
    segment.field_mapping.mappings = []
  }
  return segment
}

function normalizePatchSources(value: string | string[] | null | undefined): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || '').trim()).filter(Boolean)
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    return [value.trim()]
  }
  return []
}

function cloneConfig(config: Record<string, any>): Record<string, any> {
  return JSON.parse(JSON.stringify(config || {}))
}
